"use strict";
/**
 * Authentication API client for int-platform-auth service
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.isTokenExpired = exports.decodeToken = exports.getTenantPermissions = exports.getPermissions = exports.refreshToken = exports.login = void 0;
// Validate AUTH_API_URL is configured
const AUTH_API_URL = process.env.AUTH_API_URL;
if (!AUTH_API_URL) {
    throw new Error('AUTH_API_URL environment variable is required');
}
// Validate HTTPS in production
if (process.env.NODE_ENV === 'production' && !AUTH_API_URL.startsWith('https://')) {
    throw new Error('AUTH_API_URL must use HTTPS in production');
}
// Constants
const DEFAULT_TOKEN_EXPIRATION_BUFFER_SECONDS = 60;
const DEFAULT_REQUEST_TIMEOUT_MS = 30000;
/**
 * Fetch wrapper with timeout and secure defaults
 * @param {string} url - URL to fetch
 * @param {RequestInit} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<Response>} Fetch response
 */
const fetchWithTimeout = (url_1, ...args_1) => __awaiter(void 0, [url_1, ...args_1], void 0, function* (url, options = {}, timeout = DEFAULT_REQUEST_TIMEOUT_MS) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    try {
        const response = yield fetch(url, Object.assign(Object.assign({}, options), { mode: 'cors', credentials: 'omit', redirect: 'error', referrerPolicy: 'no-referrer', signal: controller.signal }));
        clearTimeout(timeoutId);
        return response;
    }
    catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === 'AbortError') {
            throw new Error('Request timeout. Please try again.');
        }
        if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
            throw new Error('Network error. Please check your connection.');
        }
        throw error;
    }
});
/**
 * Login with username and password
 * @param {string} username - User's username
 * @param {string} password - User's password
 * @returns {Promise<string>} JWT token from Authorization header
 */
const login = (username, password) => __awaiter(void 0, void 0, void 0, function* () {
    const credentials = { username, password };
    const response = yield fetchWithTimeout(`${AUTH_API_URL}/login`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
    });
    if (!response.ok) {
        // Don't expose server error details to prevent information disclosure
        if (response.status === 401 || response.status === 403) {
            throw new Error('Invalid username or password');
        }
        else if (response.status >= 500) {
            throw new Error('Authentication service temporarily unavailable. Please try again later.');
        }
        else {
            throw new Error('Login failed. Please check your credentials and try again.');
        }
    }
    const token = response.headers.get('Authorization');
    if (!token || typeof token !== 'string') {
        throw new Error('Invalid authentication response');
    }
    // Validate token format (JWT should have 3 parts)
    const parts = token.split('.');
    if (parts.length !== 3) {
        throw new Error('Invalid token format received from server');
    }
    // SECURITY: Validate no header injection characters
    if (token.includes('\r') || token.includes('\n')) {
        throw new Error('Invalid token: contains illegal characters');
    }
    // SECURITY: Validate JWT format (base64url + dots only)
    if (!/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token)) {
        throw new Error('Invalid token format: contains illegal characters');
    }
    return token;
});
exports.login = login;
/**
 * Refresh an expired JWT token
 * Note: This function is currently unused but reserved for future automatic token refresh
 * @param {string} token - Expired JWT token
 * @returns {Promise<string | null>} New JWT token or null if refresh failed
 */
const refreshToken = (token) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const request = { token };
        const response = yield fetchWithTimeout(`${AUTH_API_URL}/refresh`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
        });
        if (!response.ok) {
            return null;
        }
        const data = yield response.json();
        return data.refreshedToken;
    }
    catch (error) {
        return null;
    }
});
exports.refreshToken = refreshToken;
/**
 * Get user permissions from authentication service
 * @param {string} token - JWT token
 * @param {string} application - Application name (default: 'skylab')
 * @returns {Promise<Permission[]>} Array of user permissions
 */
const getPermissions = (token_1, ...args_1) => __awaiter(void 0, [token_1, ...args_1], void 0, function* (token, application = 'skylab') {
    const response = yield fetchWithTimeout(`${AUTH_API_URL}/permission/mine?application=${encodeURIComponent(application)}`, {
        headers: {
            'Authorization': token,
        },
    });
    if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
            throw new Error('Unauthorized: Invalid or expired token');
        }
        else if (response.status >= 500) {
            throw new Error('Permission service temporarily unavailable');
        }
        throw new Error(`Failed to fetch permissions (${response.status})`);
    }
    return response.json();
});
exports.getPermissions = getPermissions;
/**
 * Get tenant-specific user permissions
 * @param {string} token - JWT token
 * @param {string} application - Application name (default: 'skylab')
 * @returns {Promise<Permission[]>} Array of tenant-scoped permissions
 */
const getTenantPermissions = (token_1, ...args_1) => __awaiter(void 0, [token_1, ...args_1], void 0, function* (token, application = 'skylab') {
    const response = yield fetchWithTimeout(`${AUTH_API_URL}/permission/tenant-mine?application=${encodeURIComponent(application)}`, {
        headers: {
            'Authorization': token,
        },
    });
    if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
            throw new Error('Unauthorized: Invalid or expired token');
        }
        else if (response.status >= 500) {
            throw new Error('Permission service temporarily unavailable');
        }
        throw new Error(`Failed to fetch tenant permissions (${response.status})`);
    }
    return response.json();
});
exports.getTenantPermissions = getTenantPermissions;
/**
 * Decode JWT token to extract user information
 *
 * SECURITY CRITICAL: This is client-side base64 decode ONLY, NOT cryptographic verification.
 *
 * Token signature verification MUST be done by the backend on EVERY request.
 * The backend MUST verify:
 * - Signature validity (using secret key)
 * - Issuer (iss) claim
 * - Audience (aud) claim
 * - Expiration (exp) claim
 * - Not-before (nbf) claim if present
 *
 * Client-side decoding is ONLY for:
 * - Reading user info for UI display
 * - Checking expiration to trigger refresh
 * - Extracting claims for client-side routing
 *
 * NEVER trust client-decoded token data for authorization on the backend.
 *
 * @param {string} token - JWT token
 * @returns {DecodedToken} Decoded token payload
 */
const decodeToken = (token) => {
    // Validate input
    if (!token || typeof token !== 'string') {
        throw new Error('Invalid token: must be a non-empty string');
    }
    // SECURITY: Validate no header injection characters
    if (token.includes('\r') || token.includes('\n')) {
        throw new Error('Invalid token: contains illegal characters');
    }
    // JWT tokens must have exactly 3 parts separated by dots
    const parts = token.split('.');
    if (parts.length !== 3) {
        throw new Error('Invalid token format: expected 3 parts (header.payload.signature)');
    }
    // SECURITY: Validate JWT format (base64url + dots only)
    if (!/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token)) {
        throw new Error('Invalid token format: contains illegal characters');
    }
    try {
        const base64Url = parts[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join(''));
        const decoded = JSON.parse(jsonPayload);
        // Validate payload structure
        if (!decoded || typeof decoded !== 'object') {
            throw new Error('Invalid payload structure');
        }
        // SECURITY: Validate required claims exist
        if (!decoded.userId || !decoded.userType || !decoded.exp) {
            throw new Error('Token missing required claims');
        }
        // SECURITY: Check expiration immediately after decoding
        const currentTime = Date.now() / 1000;
        if (decoded.exp < currentTime) {
            throw new Error('Token expired');
        }
        return decoded;
    }
    catch (error) {
        // Never expose parsing errors or malformed data
        if ((error instanceof Error && error.message.startsWith('Token')) || (error instanceof Error && error.message.startsWith('Invalid'))) {
            throw error;
        }
        throw new Error('Invalid token format: unable to decode payload');
    }
};
exports.decodeToken = decodeToken;
/**
 * Check if token is expired
 * @param {string} token - JWT token
 * @param {number} bufferSeconds - Buffer time in seconds before expiration
 * @returns {boolean} True if token is expired or will expire within buffer time
 */
const isTokenExpired = (token, bufferSeconds = DEFAULT_TOKEN_EXPIRATION_BUFFER_SECONDS) => {
    try {
        const decoded = (0, exports.decodeToken)(token);
        const currentTime = Date.now() / 1000;
        // Consider token expired if it expires within the buffer time
        return decoded.exp < (currentTime + bufferSeconds);
    }
    catch (error) {
        return true;
    }
};
exports.isTokenExpired = isTokenExpired;
